/**
 * Processes content data and returns an object with content type names as keys and their entries as values.
 *
 * @param {any[]} content - The content data array.
 * @returns {Object} - The processed content data.
 */
export const processContentData = (content: any[]) => {
  return content?.length
    ? content.reduce((acc, item) => ({ ...acc, [item.contentTypeName]: item.entries }), {})
    : {};
};

/**
 * Processes configuration data and returns an object with config table names as keys and their parsed outputs as values.
 *
 * @param {any[]} config - The configuration data array.
 * @returns {Object} - The processed configuration data.
 */
export const processConfigData = (config: any[]) => {
  return config?.length
    ? config.reduce((acc, item) => {
        if (item.configTableName === 'FPO_APP_CONFIG') {
          const parsedOutput = item.output.reduce(
            (outputAcc: any, _item: { value: string; key: any }) => {
              const parsedData = _item.value ? JSON.parse(_item.value) : null;
              return { ...outputAcc, [_item.key]: parsedData };
            },
            {},
          );
          return { ...acc, [item.configTableName]: parsedOutput };
        }
        return { ...acc, [item.configTableName]: item.output };
      }, {})
    : {};
};

/**
 * Extracts roles from the user application details for the "OWNER OPERATOR" application.
 *
 * @param {any} roles - The roles data.
 * @returns {string[]} - The extracted roles.
 */
const extractRoles = (roles: any): string[] => {
  return (
    roles?.userApplicationDetails
      ?.filter((item: any) => item.applicationName === 'OWNER OPERATOR')
      ?.flatMap((item: any) => item.roles?.map((role: any) => role.roleName)) || []
  );
};

/**
 * Checks if the extracted roles match the required data.
 *
 * @param {any} requiredData - The required data.
 * @param {string[]} extractedRoles - The extracted roles.
 * @returns {boolean} - True if the roles match the required data, false otherwise.
 */
const checkRoles = (requiredData: any, extractedRoles: string[]): boolean => {
  if (Array.isArray(requiredData)) {
    return extractedRoles.some((role) => requiredData.includes(role));
  } else if (typeof requiredData === 'object' && requiredData !== null) {
    if (
      requiredData.excluded &&
      extractedRoles.some((role) => requiredData.excluded.includes(role))
    ) {
      return false;
    }
    if (requiredData.allowed) {
      return extractedRoles.some((role) => requiredData.allowed.includes(role));
    }
  }
  return false;
};

/**
 * Determines if the user is entitled based on their roles and the required data for a specific section and sub-section.
 *
 * @param {any} roles - The roles data.
 * @param {any} data - The data containing required roles information.
 * @param {string} section - The section to check.
 * @param {string} subSection - The sub-section to check.
 * @returns {boolean} - True if the user is entitled, false otherwise.
 */

export const isEntitled = (
  roles: any,
  data: any,
  section: string,
  subSection: string = '',
): boolean => {
  const requiredData = subSection ? data?.[section]?.[subSection] : data?.[section];
  if (!requiredData) return false;
  const extractedRoles = extractRoles(roles);
  return extractedRoles.length ? checkRoles(requiredData, extractedRoles) : false;
};

export const isAdvanceSearch = (data: any, subSection: string): boolean => {
  const hasSection = data?.['search']?.[subSection]?.['advancedSearch'];
  if (!hasSection) return false;
  return hasSection.length ? true : false;
};
